import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import DataTable from 'react-data-table-component';
import { Modal } from 'react-bootstrap';

import { informacionRevision, confirmarRevision, limpiarRevision } from '../../actions/revisiones';
import { editarBreadcrumb } from '../../actions/breadcrumb';

const formatFecha = (fecha) => fecha
    ? new Date(fecha).toLocaleDateString('es-MX', { day: '2-digit', month: 'long', year: 'numeric' })
    : '-';

const formatFechaHora = (fecha) => fecha
    ? new Date(fecha).toLocaleString('es-MX', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })
    : '-';

const formatNombre = (persona) => persona
    ? `${persona.nombre || ''} ${persona.apellido_paterno || ''} ${persona.apellido_materno || ''}`.trim()
    : '-';

const InformacionRevision = () => {

    const dispatch = useDispatch();
    const { id } = useParams();

    const informacion = useSelector(state => state.revisiones.informacion);

    const [confirmarModal, setConfirmarModal] = useState(false);
    const [observaciones, setObservaciones] = useState('');
    const [statusProcesar, setStatusProcesar] = useState(false);

    useEffect(() => {
        if (id) {
            dispatch(informacionRevision(id));
            dispatch(editarBreadcrumb([
                { nombre: 'Revisiones', path: '/revisiones', activo: false },
                { nombre: 'Información de revisión', path: `/revisiones/informacion/${id}`, activo: true }
            ]));
        }

        return () => {
            dispatch(limpiarRevision());
        };
    }, [id]);

    const handleAbrirConfirmar = () => {
        setObservaciones('');
        setConfirmarModal(true);
    };

    const handleCerrarConfirmar = () => {
        setObservaciones('');
        setConfirmarModal(false);
    };

    const handleConfirmar = async () => {
        setStatusProcesar(true);
        await dispatch(confirmarRevision(informacion._id, observaciones));
        setStatusProcesar(false);
        setConfirmarModal(false);
    };

    const columnsPuntos = [
        {
            name: 'Punto de revisión',
            sortable: true,
            selector: row => row.nombre || '-',
            grow: 3
        },
        {
            name: 'Status',
            sortable: true,
            cell: row => (
                <span className={`badge ${row.status === true ? 'bg-success' : 'bg-danger'}`}>
                    {row.status === true ? 'Bien' : 'Mal'}
                </span>
            )
        }
    ];

    const puntosOk = (informacion?.puntos || []).filter(p => p.status === true).length;
    const puntosMal = (informacion?.puntos || []).filter(p => p.status === false).length;

    return (
        <div className="row mt-5">

            {/* Header */}
            <div className="col-12 mb-4">
                <div className="d-flex align-items-center gap-3">
                    <div className="rounded-circle bg-secondary bg-opacity-10 d-flex align-items-center justify-content-center" style={{ width: 48, height: 48, minWidth: 48 }}>
                        <i className="fas fa-tasks text-primary fs-5"></i>
                    </div>
                    <div>
                        <h4 className="mb-0 fw-semibold">
                            {informacion?.sucursal?.nombre
                                ? `Revisión — ${informacion.sucursal.nombre}`
                                : 'Información de revisión'}
                        </h4>
                        <small className="text-muted">{formatFechaHora(informacion?.fecha_creacion)}</small>
                    </div>
                    <div className="ms-auto d-flex align-items-center gap-2">
                        <span className={`badge fs-6 px-3 py-2 ${informacion?.status === true ? 'bg-success' : 'bg-warning text-dark'}`}>
                            <i className={`fas ${informacion?.status === true ? 'fa-check-circle' : 'fa-clock'} me-1`}></i>
                            {informacion?.status === true ? 'Revisada' : 'Pendiente'}
                        </span>
                        {informacion?.status === false && (
                            <button className="btn btn-success" onClick={handleAbrirConfirmar}>
                                <i className="fas fa-check me-1"></i>
                                Confirmar revisión
                            </button>
                        )}
                    </div>
                </div>
            </div>

            {/* KPIs */}
            <div className="col-md mb-3">
                <div className="card border-0 shadow-sm h-100">
                    <div className="card-body d-flex align-items-center gap-3">
                        <div className="rounded-circle bg-info bg-opacity-10 d-flex align-items-center justify-content-center" style={{ width: 44, height: 44, minWidth: 44 }}>
                            <i className="fas fa-store text-primary"></i>
                        </div>
                        <div>
                            <div className="text-muted small">Sucursal</div>
                            <div className="fw-bold">{informacion?.sucursal?.nombre || '-'}</div>
                        </div>
                    </div>
                </div>
            </div>
            <div className="col-md mb-3">
                <div className="card border-0 shadow-sm h-100">
                    <div className="card-body d-flex align-items-center gap-3">
                        <div className="rounded-circle bg-secondary bg-opacity-10 d-flex align-items-center justify-content-center" style={{ width: 44, height: 44, minWidth: 44 }}>
                            <i className="fas fa-user text-secondary"></i>
                        </div>
                        <div>
                            <div className="text-muted small">Responsable</div>
                            <div className="fw-bold">{formatNombre(informacion?.responsable)}</div>
                        </div>
                    </div>
                </div>
            </div>
            <div className="col-md mb-3">
                <div className="card border-0 shadow-sm h-100">
                    <div className="card-body d-flex align-items-center gap-3">
                        <div className="rounded-circle bg-success bg-opacity-10 d-flex align-items-center justify-content-center" style={{ width: 44, height: 44, minWidth: 44 }}>
                            <i className="fas fa-check-circle text-success"></i>
                        </div>
                        <div>
                            <div className="text-muted small">Puntos correctos</div>
                            <div className="fw-bold fs-5">{puntosOk} / {informacion?.puntos?.length || 0}</div>
                        </div>
                    </div>
                </div>
            </div>
            <div className="col-md mb-3">
                <div className="card border-0 shadow-sm h-100">
                    <div className="card-body d-flex align-items-center gap-3">
                        <div className="rounded-circle bg-danger bg-opacity-10 d-flex align-items-center justify-content-center" style={{ width: 44, height: 44, minWidth: 44 }}>
                            <i className="fas fa-times-circle text-danger"></i>
                        </div>
                        <div>
                            <div className="text-muted small">Puntos con falla</div>
                            <div className="fw-bold fs-5">{puntosMal}</div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Información general */}
            <div className="col-12 mb-3">
                <div className="card border-0 shadow-sm">
                    <div className="card-header bg-white border-bottom py-3">
                        <h6 className="mb-0 fw-semibold">
                            <i className="fas fa-info-circle text-primary me-2"></i>
                            Información general
                        </h6>
                    </div>
                    <div className="card-body">
                        <div className="row g-3">
                            <div className="col-md-6">
                                <div className="border-start border-primary border-3 ps-3">
                                    <div className="text-muted small mb-1">Sucursal</div>
                                    <div className="fw-semibold">{informacion?.sucursal?.nombre || '-'}</div>
                                </div>
                            </div>
                            <div className="col-md-6">
                                <div className="border-start border-primary border-3 ps-3">
                                    <div className="text-muted small mb-1">Responsable</div>
                                    <div className="fw-semibold">{formatNombre(informacion?.responsable)}</div>
                                </div>
                            </div>
                            <div className="col-md-6">
                                <div className="border-start border-secondary border-3 ps-3">
                                    <div className="text-muted small mb-1">Fecha de creación</div>
                                    <div className="fw-semibold">{formatFechaHora(informacion?.fecha_creacion)}</div>
                                </div>
                            </div>
                            <div className="col-md-6">
                                <div className="border-start border-secondary border-3 ps-3">
                                    <div className="text-muted small mb-1">Fecha de revisión</div>
                                    <div className="fw-semibold">{informacion?.fecha_revision ? formatFechaHora(informacion.fecha_revision) : 'Pendiente'}</div>
                                </div>
                            </div>
                            <div className="col-md-6">
                                <div className="border-start border-info border-3 ps-3">
                                    <div className="text-muted small mb-1">Confirmador</div>
                                    <div className="fw-semibold">{informacion?.confirmador ? formatNombre(informacion.confirmador) : 'Pendiente'}</div>
                                </div>
                            </div>
                            <div className="col-md-6">
                                <div className="border-start border-success border-3 ps-3">
                                    <div className="text-muted small mb-1">Status</div>
                                    <div>
                                        <span className={`badge ${informacion?.status === true ? 'bg-success' : 'bg-warning text-dark'}`}>
                                            {informacion?.status === true ? 'Revisada' : 'Pendiente'}
                                        </span>
                                    </div>
                                </div>
                            </div>
                            <div className="col-12">
                                <div className="border-start border-warning border-3 ps-3">
                                    <div className="text-muted small mb-1">Observaciones de creación</div>
                                    <div className="fw-semibold">{informacion?.observaciones_creacion || 'Sin definir'}</div>
                                </div>
                            </div>
                            <div className="col-12">
                                <div className="border-start border-warning border-3 ps-3">
                                    <div className="text-muted small mb-1">Observaciones de revisión</div>
                                    <div className="fw-semibold">{informacion?.observaciones_revision || 'Sin definir'}</div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Puntos de revisión */}
            <div className="col-12">
                <div className="card border-0 shadow-sm">
                    <div className="card-header bg-white border-bottom py-3 d-flex align-items-center justify-content-between">
                        <h6 className="mb-0 fw-semibold">
                            <i className="fas fa-map-marker-alt text-warning me-2"></i>
                            Puntos de revisión
                        </h6>
                        {informacion?.puntos?.length > 0 && (
                            <span className="badge bg-warning text-dark rounded-pill">{informacion.puntos.length}</span>
                        )}
                    </div>
                    <div className="card-body p-0">
                        <DataTable
                            columns={columnsPuntos}
                            data={informacion?.puntos || []}
                            pagination
                            paginationPerPage={20}
                            paginationComponentOptions={{ rowsPerPageText: 'Filas por página', rangeSeparatorText: 'de' }}
                            highlightOnHover
                            noDataComponent={
                                <div className="py-5 text-center text-muted">
                                    <i className="fas fa-map-marker-alt fa-2x mb-2 d-block opacity-25"></i>
                                    No hay puntos de revisión registrados
                                </div>
                            }
                        />
                    </div>
                </div>
            </div>

            {/* Modal confirmar revisión */}
            <Modal show={confirmarModal} backdrop="static" centered onHide={handleCerrarConfirmar}>
                <Modal.Header closeButton>
                    <Modal.Title>Confirmar revisión</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p className="text-muted small mb-3">
                        Al confirmar esta revisión se marcará como <strong>Revisada</strong>. Esta acción no se puede deshacer.
                    </p>
                    <div className="form-floating">
                        <textarea
                            className="form-control"
                            placeholder="Observaciones de revisión"
                            id="observaciones"
                            style={{ height: '120px' }}
                            value={observaciones}
                            onChange={({ target }) => setObservaciones(target.value)}
                        ></textarea>
                        <label htmlFor="observaciones">Observaciones de revisión</label>
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <button className="btn btn-secondary" onClick={handleCerrarConfirmar} disabled={statusProcesar}>
                        Cancelar
                    </button>
                    {statusProcesar
                        ? <button className="btn btn-success" disabled>
                            <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                            Confirmando
                          </button>
                        : <button className="btn btn-success" onClick={handleConfirmar}>
                            <i className="fas fa-check me-1"></i>
                            Confirmar
                          </button>
                    }
                </Modal.Footer>
            </Modal>

        </div>
    );
};

export default InformacionRevision;
